import {
  isAppleAuthSupported,
  isFacebookAvailable,
  isGoogleAvailable,
} from '@wlb/shared/platform/socialAuth';
import type { SocialAuthResult } from '@wlb/shared/types';

export type SocialProvider = 'google' | 'facebook' | 'apple';

const PROVIDER_NAMES: Record<SocialProvider, 'Google' | 'Facebook' | 'Apple'> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

const PROVIDER_LABEL_KEYS: Record<SocialProvider, string> = {
  google: 'auth.social.continueGoogle',
  facebook: 'auth.social.continueFacebook',
  apple: 'auth.social.continueApple',
};

/** The `signUpMethod` value the SSO endpoints expect for a provider. */
export const socialProviderName = (provider: SocialProvider): 'Google' | 'Facebook' | 'Apple' =>
  PROVIDER_NAMES[provider];

/**
 * Full-sentence button label. `auth.social.<provider>` is the short label the
 * mobile screens render next to their own icon, so web uses its own keys.
 */
export const socialProviderLabelKey = (provider: SocialProvider): string =>
  PROVIDER_LABEL_KEYS[provider];

/**
 * Providers whose client id is configured. Without one the SDK can never
 * complete a sign-in, so the button is hidden rather than rendered broken.
 */
export const AVAILABLE_SOCIAL_PROVIDERS: SocialProvider[] = [
  ...(isGoogleAvailable() ? (['google'] as const) : []),
  ...(isFacebookAvailable() ? (['facebook'] as const) : []),
  ...(isAppleAuthSupported() ? (['apple'] as const) : []),
];

/**
 * Mirrors the token precedence the mobile auth screens use so both platforms
 * hand the same credential to LoginSSO / CreateAccountSSO.
 */
export const socialAuthToken = (provider: SocialProvider, result: SocialAuthResult): string => {
  if (provider === 'google') {
    return result.idToken || result.serverAuthCode || result.accessToken || '';
  }
  if (provider === 'facebook') {
    return result.accessToken || '';
  }
  return result.idToken || result.accessToken || '';
};
